import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Combobox } from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { BodyMap } from "@/components/worker/BodyMap";
import { orgNowLocal, orgToday } from "@/lib/tz";
import { AlertTriangle, ImagePlus, Loader2, Plus, ShieldAlert, Trash2, X } from "lucide-react";
import { toast } from "sonner";

// Office version of the worker's IncidentForm: the same report, linked to a shift
// (naming anyone on it) or on its own. Saved through POST /incidents.

const TYPES = [
  ["behaviour", "Behaviour"], ["injury", "Injury"], ["self_harm", "Self Harm"],
  ["medication", "Medication"], ["vehicle", "Vehicle"], ["other", "Other"],
];
const SEVERITIES = ["Low", "Medium", "High", "Critical"];
const SEV_ON = {
  Low: "bg-slate-600 text-white border-slate-600", Medium: "bg-[#bf3b4f] text-white border-[#bf3b4f]",
  High: "bg-orange-600 text-white border-orange-600", Critical: "bg-red-700 text-white border-red-700",
};

const blankForm = () => ({
  types: [], participant: "", support_worker: "", incident_datetime: orgNowLocal().format("YYYY-MM-DDTHH:mm"),
  location: "", severity: "Medium", leading_up: "", description: "", immediate_action: "", witnesses: "",
  behaviour_details: "", injury_party: "", self_harm_details: "", medication_details: "",
  vehicle_rego: "", vehicle_details: "", police_attended: null, police_report_number: "", other_details: "",
});
const blankInjury = () => ({
  person_type: "Participant", person_name: "", body_location: "", body_map: [], nature_of_injury: "",
  first_aid_given: "", medical_treatment_sought: false, hospital: false,
});

const lbl = "text-[11px] font-semibold text-slate-700 mb-1 block";
const req = <span className="text-red-600">*</span>;
const chip = (on, onCls = "bg-[#bf3b4f] text-white border-[#bf3b4f]") =>
  `text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${on ? onCls : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`;
const card = "bg-white border border-slate-200 rounded-lg p-4 space-y-3";
const fmtTime = (t) => (t ? dayjs(t).format("HH:mm") : "");

export default function IncidentNew() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [params] = useSearchParams();
  const presetShift = params.get("shift");
  const [onShift, setOnShift] = useState(!!presetShift);
  const [shiftDate, setShiftDate] = useState(orgToday());
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [shiftName, setShiftName] = useState(presetShift || "");
  const [options, setOptions] = useState({ participants: [], staff: [] });
  const [f, setF] = useState(blankForm);
  const [injuries, setInjuries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const has = (t) => f.types.includes(t);
  const toggleType = (t) => setF((s) => ({ ...s, types: s.types.includes(t) ? s.types.filter((x) => x !== t) : [...s.types, t] }));
  const setInj = (i, k, v) => setInjuries((a) => a.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  useEffect(() => {
    api.incidentFormOptions().then(setOptions).catch(() => toast.error("Could not load participants and staff"));
  }, []);

  // A shift passed in the address (eg. from a timesheet) opens on that shift's day.
  useEffect(() => {
    if (!presetShift) return;
    api.incidentShiftOptions({ shift: presetShift }).then((rows) => {
      if (rows[0]?.shift_date) setShiftDate(rows[0].shift_date);
    }).catch(() => { toast.error("That shift wasn't found"); setShiftName(""); });
  }, [presetShift]);

  useEffect(() => {
    if (!onShift || !shiftDate) return;
    setLoadingShifts(true);
    api.incidentShiftOptions({ date: shiftDate }).then(setShifts).catch(() => setShifts([]))
      .finally(() => setLoadingShifts(false));
  }, [onShift, shiftDate]);

  const shift = shifts.find((s) => s.name === shiftName);
  const pickShift = (name) => { setShiftName(name); setF((s) => ({ ...s, participant: "", support_worker: "" })); };
  const switchMode = (v) => { setOnShift(v); setShiftName(""); setF((s) => ({ ...s, participant: "", support_worker: "" })); };

  const pOpts = useMemo(() => [{ value: "", label: "None" }, ...options.participants], [options]);
  const sOpts = useMemo(() => [{ value: "", label: "None" }, ...options.staff], [options]);

  const addPhotos = (fileList) => {
    const files = Array.from(fileList || []).filter((x) => x.type.startsWith("image/"));
    setPhotos((p) => [...p, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };
  const removePhoto = (i) => setPhotos((p) => { URL.revokeObjectURL(p[i].url); return p.filter((_, idx) => idx !== i); });

  const submit = async () => {
    if (onShift && !shiftName) return toast.error("Pick the shift, or choose Not on a shift");
    if (f.types.length === 0) return toast.error("Select at least one incident type");
    if (!f.incident_datetime) return toast.error("Set the incident date/time");
    if (!f.leading_up.trim()) return toast.error("Describe what happened leading up to the event");
    if (!f.description.trim()) return toast.error("Describe what happened");
    if (!f.immediate_action.trim()) return toast.error("Record the immediate action taken");
    if (has("vehicle") && f.police_attended === null) return toast.error("Did the police attend the vehicle incident?");
    const payload = {
      ...f,
      shift: onShift ? shiftName : null,
      participant: f.participant || null, support_worker: f.support_worker || null,
      police_attended: has("vehicle") ? !!f.police_attended : false,
      police_report_number: has("vehicle") && f.police_attended ? f.police_report_number.trim() : "",
      incident_datetime: f.incident_datetime.replace("T", " ") + ":00",
      injuries: has("injury") ? injuries.map((i) => ({ ...i, body_map: JSON.stringify(i.body_map || []) })) : [],
    };
    setBusy(true);
    let res;
    try {
      res = await api.officeAddIncident(payload);
    } catch (e) {
      setBusy(false);
      return toast.error(e.response?.data?.detail || "Could not log the incident");
    }
    // One photo per upload, so a big batch never hits the server's request limit.
    let failed = 0;
    for (const p of photos) {
      const fd = new FormData();
      fd.append("photos", p.file);
      try { await api.officeAddIncidentPhotos(res.name, fd); } catch { failed += 1; }
    }
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    if (failed) toast.error(`Incident logged, but ${failed} photo${failed === 1 ? "" : "s"} didn't upload`);
    toast.success(`Incident ${res.name} logged`);
    setBusy(false);
    if (can.has("incidents.view")) nav(`/incidents/${res.name}`);
    else { setF(blankForm()); setInjuries([]); setPhotos([]); setShiftName(""); window.scrollTo(0, 0); }
  };

  return (
    <div className="p-4 max-w-3xl" data-testid="incident-new">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="h-5 w-5 text-red-600" />
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Log an incident</h1>
          <p className="text-[11px] text-slate-400">Report an incident from the office. It goes to the incident managers to review.</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className={card}>
          <div>
            <span className={lbl}>Did it happen on a shift? {req}</span>
            <div className="flex gap-2">
              <button type="button" className={chip(onShift)} onClick={() => switchMode(true)} data-testid="inc-new-on-shift">On a shift</button>
              <button type="button" className={chip(!onShift)} onClick={() => switchMode(false)} data-testid="inc-new-no-shift">Not on a shift</button>
            </div>
          </div>

          {onShift ? (
            <>
              <div className="max-w-[12rem]">
                <span className={lbl}>Shift date</span>
                <Input type="date" value={shiftDate} onChange={(e) => { setShiftDate(e.target.value); pickShift(""); }}
                  className="h-9 text-xs" data-testid="inc-new-shift-date" />
              </div>
              <div>
                <span className={lbl}>Shift {req}</span>
                {loadingShifts ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  : shifts.length === 0 ? <div className="text-xs text-slate-400" data-testid="inc-new-no-shifts">No shifts on this day.</div>
                  : (
                    <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto" data-testid="inc-new-shifts">
                      {shifts.map((s) => (
                        <button key={s.name} type="button" onClick={() => pickShift(s.name)} data-testid={`inc-new-shift-${s.name}`}
                          className={`text-left rounded-md border p-2 text-xs transition-colors ${shiftName === s.name ? "border-[#bf3b4f] bg-rose-50" : "border-slate-200 hover:border-slate-300"}`}>
                          <div className="font-semibold text-slate-800">{fmtTime(s.start_time)}–{fmtTime(s.end_time)} <span className="font-mono font-normal text-[10px] text-slate-400">{s.name}</span></div>
                          <div className="text-slate-600 truncate">{s.participants.map((p) => p.participant_name || p.participant).join(", ") || "No participant"}</div>
                          <div className="text-slate-400 truncate">{s.workers.map((w) => w.label).join(", ") || "No worker"}</div>
                        </button>
                      ))}
                    </div>
                  )}
              </div>
              {shift && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <span className={lbl}>Participant involved</span>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={chip(!f.participant)} onClick={() => set("participant", "")} data-testid="inc-new-participant-none">None</button>
                      {shift.participants.map((p) => (
                        <button key={p.participant} type="button" className={chip(f.participant === p.participant)}
                          onClick={() => set("participant", p.participant)} data-testid={`inc-new-participant-${p.participant}`}>
                          {p.participant_name || p.participant}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className={lbl}>Worker involved</span>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={chip(!f.support_worker)} onClick={() => set("support_worker", "")} data-testid="inc-new-worker-none">None</button>
                      {shift.workers.map((w) => (
                        <button key={w.value} type="button" className={chip(f.support_worker === w.value)}
                          onClick={() => set("support_worker", w.value)} data-testid={`inc-new-worker-${w.value}`}>{w.label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <span className={lbl}>Participant involved</span>
                <Combobox testid="inc-new-participant" options={pOpts} value={f.participant} onChange={(v) => set("participant", v)} placeholder="None" />
              </div>
              <div>
                <span className={lbl}>Staff member involved</span>
                <Combobox testid="inc-new-staff" options={sOpts} value={f.support_worker} onChange={(v) => set("support_worker", v)} placeholder="None" />
              </div>
            </div>
          )}
        </div>

        <div className={card}>
          <div>
            <span className={lbl}>Incident type(s) {req}</span>
            <div className="flex flex-wrap gap-2">
              {TYPES.map(([k, label]) => (
                <button key={k} type="button" className={chip(has(k))} onClick={() => toggleType(k)} data-testid={`inc-new-type-${k}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className={lbl}>Date & time {req}</span>
              <Input type="datetime-local" value={f.incident_datetime} onChange={(e) => set("incident_datetime", e.target.value)}
                className="h-9 text-xs" data-testid="inc-new-datetime" />
            </div>
            <div>
              <span className={lbl}>Location</span>
              <Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Where did it happen?"
                className="h-9 text-xs" data-testid="inc-new-location" />
            </div>
          </div>
          <div>
            <span className={lbl}>Severity</span>
            <div className="flex gap-2">
              {SEVERITIES.map((sv) => (
                <button key={sv} type="button" className={chip(f.severity === sv, SEV_ON[sv])} onClick={() => set("severity", sv)} data-testid={`inc-new-sev-${sv}`}>{sv}</button>
              ))}
            </div>
          </div>
          {(f.severity === "High" || f.severity === "Critical") && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800" data-testid="inc-new-escalation">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Incident managers are emailed as soon as you log this. If anyone is at risk, act first and tell a manager now.</span>
            </div>
          )}
        </div>

        <div className={card}>
          <div>
            <span className={lbl}>What happened leading up to the event {req}</span>
            <Textarea rows={2} value={f.leading_up} onChange={(e) => set("leading_up", e.target.value)} className="text-xs"
              placeholder="Context / triggers before the incident…" data-testid="inc-new-leading" />
          </div>
          <div>
            <span className={lbl}>Description of what happened {req}</span>
            <Textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className="text-xs"
              placeholder="Describe the incident in detail…" data-testid="inc-new-description" />
          </div>
          <div>
            <span className={lbl}>Immediate action taken {req}</span>
            <Textarea rows={2} value={f.immediate_action} onChange={(e) => set("immediate_action", e.target.value)} className="text-xs"
              placeholder="What was done straight away?" data-testid="inc-new-action" />
          </div>
          <div>
            <span className={lbl}>Witnesses</span>
            <Input value={f.witnesses} onChange={(e) => set("witnesses", e.target.value)} className="h-9 text-xs"
              placeholder="Names of anyone who saw it" data-testid="inc-new-witnesses" />
          </div>

          {has("behaviour") && (
            <div>
              <span className={lbl}>Behaviour details</span>
              <Textarea rows={2} value={f.behaviour_details} onChange={(e) => set("behaviour_details", e.target.value)} className="text-xs" data-testid="inc-new-behaviour" />
            </div>
          )}
          {has("injury") && (
            <div className="rounded-md border border-slate-200 p-3 space-y-3" data-testid="inc-new-sec-injury">
              <div>
                <span className={lbl}>Who was injured</span>
                <div className="flex gap-2">
                  {["Staff", "Participant", "Both"].map((p) => (
                    <button key={p} type="button" className={chip(f.injury_party === p)} onClick={() => set("injury_party", p)} data-testid={`inc-new-injparty-${p}`}>{p}</button>
                  ))}
                </div>
              </div>
              {injuries.map((inj, i) => (
                <div key={i} className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`inc-new-injury-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {["Staff", "Participant"].map((pt) => (
                        <button key={pt} type="button" className={chip(inj.person_type === pt)} onClick={() => setInj(i, "person_type", pt)}>{pt}</button>
                      ))}
                    </div>
                    <button type="button" onClick={() => setInjuries((a) => a.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <Input value={inj.person_name} onChange={(e) => setInj(i, "person_name", e.target.value)} placeholder="Name" className="h-9 text-xs bg-white" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={inj.body_location} onChange={(e) => setInj(i, "body_location", e.target.value)} placeholder="Body location (notes)" className="h-9 text-xs bg-white" />
                    <Input value={inj.nature_of_injury} onChange={(e) => setInj(i, "nature_of_injury", e.target.value)} placeholder="Nature of injury" className="h-9 text-xs bg-white" />
                  </div>
                  {/* The body map is drawn for the dark worker app. */}
                  <div className="rounded-md bg-slate-800 p-2.5 max-w-md">
                    <BodyMap testid={`inc-new-injury-${i}-bodymap`} value={inj.body_map} onChange={(v) => setInj(i, "body_map", v)} />
                  </div>
                  <Input value={inj.first_aid_given} onChange={(e) => setInj(i, "first_aid_given", e.target.value)} placeholder="First aid given" className="h-9 text-xs bg-white" />
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <Checkbox checked={inj.medical_treatment_sought} onCheckedChange={(v) => setInj(i, "medical_treatment_sought", !!v)} /> Medical treatment sought
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                      <Checkbox checked={inj.hospital} onCheckedChange={(v) => setInj(i, "hospital", !!v)} /> Hospital / ambulance
                    </label>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setInjuries((a) => [...a, blankInjury()])} data-testid="inc-new-injury-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add injured person
              </Button>
            </div>
          )}
          {has("self_harm") && (
            <div>
              <span className={lbl}>Self harm details</span>
              <Textarea rows={2} value={f.self_harm_details} onChange={(e) => set("self_harm_details", e.target.value)} className="text-xs" />
            </div>
          )}
          {has("medication") && (
            <div>
              <span className={lbl}>Medication error / issue details</span>
              <Textarea rows={2} value={f.medication_details} onChange={(e) => set("medication_details", e.target.value)} className="text-xs" />
            </div>
          )}
          {has("vehicle") && (
            <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="inc-new-sec-vehicle">
              <div>
                <span className={lbl}>Vehicle registration</span>
                <Input value={f.vehicle_rego} onChange={(e) => set("vehicle_rego", e.target.value)} placeholder="Rego" className="h-9 text-xs max-w-[12rem]" />
              </div>
              <div>
                <span className={lbl}>Vehicle incident details</span>
                <Textarea rows={2} value={f.vehicle_details} onChange={(e) => set("vehicle_details", e.target.value)} className="text-xs" />
              </div>
              <div>
                <span className={lbl}>Did the police attend? {req}</span>
                <div className="flex gap-2">
                  {[["Yes", true], ["No", false]].map(([t, v]) => (
                    <button key={t} type="button" className={chip(f.police_attended === v)} onClick={() => set("police_attended", v)} data-testid={`inc-new-police-${v ? "yes" : "no"}`}>{t}</button>
                  ))}
                </div>
              </div>
              {f.police_attended === true && (
                <div>
                  <span className={lbl}>Police report number <span className="text-slate-400 font-normal">(optional)</span></span>
                  <Input value={f.police_report_number} onChange={(e) => set("police_report_number", e.target.value)} placeholder="e.g. QP2400123" className="h-9 text-xs max-w-[12rem]" />
                </div>
              )}
            </div>
          )}
          {has("other") && (
            <div>
              <span className={lbl}>Other details</span>
              <Textarea rows={2} value={f.other_details} onChange={(e) => set("other_details", e.target.value)} className="text-xs" />
            </div>
          )}
        </div>

        <div className={card} data-testid="inc-new-photos">
          <span className={lbl}>Photos <span className="text-slate-400 font-normal">(optional)</span></span>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.url} alt="attachment" className="h-16 w-16 rounded-md object-cover border border-slate-200" />
                  <button type="button" onClick={() => removePhoto(i)}
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center border border-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <label className="flex items-center gap-1.5 w-fit cursor-pointer rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50">
            <ImagePlus className="h-3.5 w-3.5" /> Add photos
            <input type="file" accept="image/*" multiple className="hidden" data-testid="inc-new-photo-input"
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => nav(-1)}>Cancel</Button>
          <Button size="sm" className="h-9 text-xs bg-[#bf3b4f] hover:bg-[#a32f42]" onClick={submit} disabled={busy} data-testid="inc-new-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log incident"}
          </Button>
        </div>
      </div>
    </div>
  );
}
